import * as React from 'react';
import { parse } from 'date-fns';
import axios from 'axios';

import type { LinkParams } from 'components/ferret/Ferret.utils';

export interface FerretPrefillProps {
  departurePositionSelectedSuggestion?: PositionInfo;
  arrivalPositionSelectedSuggestion?: PositionInfo;
  departureDate?: string;
  returnDate?: string;
  tripType?: 'roundTrip' | 'oneWay';
}

export interface PositionInfo {
  positionId: number;
  displayName: string;
  defaultName: string;
  fullName: string;
}

interface PositionResponse {
  displayName?: string;
  defaultName?: string;
  countryNameInUserLocale?: string;
}

interface PrefillFerretResult {
  arrivalInfo: PositionInfo | null;
  departureInfo: PositionInfo | null;
  prefillProps: FerretPrefillProps;
}

interface UsePrefillFerretParams extends LinkParams {
  locale: string;
}

function formatDateToISO(dateValue: string): string {
  return parse(dateValue).toISOString();
}

async function fetchPositionInfo(
  id: number,
  locale: string
): Promise<PositionInfo | null> {
  try {
    const { data } = await axios.get<PositionResponse[]>(
      '/suggester-api/v5/position',
      { params: { id, locale } }
    );
    const position = data?.[0];

    if (!position?.displayName) return null;

    const countryName = position.countryNameInUserLocale;

    return {
      positionId: id,
      displayName: position.displayName,
      defaultName: position.defaultName || position.displayName,
      fullName: countryName
        ? `${position.displayName}, ${countryName}`
        : position.displayName,
    };
  } catch {
    return null;
  }
}

function usePrefillFerret({
  locale,
  departurePos,
  arrivalPos,
  departureDate,
  returnDate,
}: UsePrefillFerretParams): PrefillFerretResult {
  const [departureInfo, setDepartureInfo] = React.useState<PositionInfo | null>(
    null
  );
  const [arrivalInfo, setArrivalInfo] = React.useState<PositionInfo | null>(
    null
  );

  React.useEffect(() => {
    let isCurrent = true;

    setArrivalInfo(null);
    setDepartureInfo(null);

    async function loadPosition(
      positionId: number | null,
      setPosition: React.Dispatch<React.SetStateAction<PositionInfo | null>>
    ): Promise<void> {
      if (!positionId) return;

      const position = await fetchPositionInfo(positionId, locale);
      if (isCurrent) setPosition(position);
    }

    void loadPosition(arrivalPos, setArrivalInfo);
    void loadPosition(departurePos, setDepartureInfo);

    return () => {
      isCurrent = false;
    };
  }, [departurePos, arrivalPos, locale]);

  return {
    arrivalInfo,
    departureInfo,
    prefillProps: {
      ...(departureDate
        ? { departureDate: formatDateToISO(departureDate) }
        : {}),
      ...(returnDate
        ? { returnDate: formatDateToISO(returnDate), tripType: 'roundTrip' }
        : {}),
      ...(departureInfo
        ? { departurePositionSelectedSuggestion: departureInfo }
        : {}),
      ...(arrivalInfo
        ? { arrivalPositionSelectedSuggestion: arrivalInfo }
        : {}),
    },
  };
}

export default usePrefillFerret;
